import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';
import { Header } from '../components/Header';
import {
  FleetListingFilters,
  FleetListingItem,
  FleetListingPageResponse,
  fleetListingService
} from '../services/fleetListingService';
import { AutoBoletoResponse, billingService } from '../services/billingService';

// Mapa de transições permitidas: status atual → próximo status.
// A transição para 'Faturado' ocorre automaticamente via emissão de boleto.
// Nesta tela, apenas a confirmação de pagamento é feita manualmente.
const TRANSITION_MAP: Record<string, string> = {
  Faturado: 'Pago',
};

const getTargetStatus = (status: string): string | null => TRANSITION_MAP[status] ?? null;

// Status elegíveis para emissão de boleto (fechamento de fatura)
const canEmitBoleto = (status: string): boolean =>
  status === 'Estoque' || status === 'Aguardando Faturamento';

const initialFilters: FleetListingFilters = {
  dealerNetworkCnpj: '',
  dealerCnpj: '',
  start: '',
  end: '',
  status: '',
  situacao: ''
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const formatDateTime = (value: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
};

const formatCnpj = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (!digits) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const unmaskCnpj = (value: string): string => value.replace(/\D/g, '');

const parseDateOnlyToUtc = (value: string): Date | null => {
  const [yearStr, monthStr, dayStr] = value.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

interface ReportFormState {
  period: string;
  start: string;
  end: string;
}

export const FleetListingAdminPage: React.FC = () => {
  const [filters, setFilters] = useState<FleetListingFilters>(initialFilters);
  const [cnpjType, setCnpjType] = useState<'dealerNetwork' | 'dealer'>('dealerNetwork');
  const [cnpj, setCnpj] = useState('');
  const [items, setItems] = useState<FleetListingItem[]>([]);
  const [pageState, setPageState] = useState<FleetListingPageResponse>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 5,
    number: 0,
    first: true,
    last: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Seleção para atualização em lote
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState<string | null>(null);

  // Relatório
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportModalMessage, setReportModalMessage] = useState<string | null>(null);
  const [reportForm, setReportForm] = useState<ReportFormState>({
    period: 'FROM',
    start: '',
    end: ''
  });

  // Emissão de boleto (fechamento de fatura)
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [billingDueDate, setBillingDueDate] = useState('');
  const [isBilling, setIsBilling] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingResult, setBillingResult] = useState<AutoBoletoResponse | null>(null);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        cnpj.trim() ||
          filters.dealerNetworkCnpj ||
          filters.dealerCnpj ||
          filters.start ||
          filters.end ||
          filters.status ||
          filters.situacao
      ),
    [cnpj, filters]
  );

  // Status-alvo ativo: determinado pelo primeiro item selecionado.
  // Enquanto houver seleção, apenas itens com o mesmo status-alvo podem ser marcados.
  const activeTargetStatus = useMemo<string | null>(() => {
    const firstId = Array.from(selectedIds)[0];
    if (!firstId) return null;
    const firstItem = items.find((i) => i.id === firstId);
    return firstItem ? getTargetStatus(firstItem.status) : null;
  }, [selectedIds, items]);

  // Verifica se a seleção atual pode emitir boleto
  const canSelectedEmitBoleto = useMemo(() => {
    if (selectedIds.size === 0) return false;
    const firstId = Array.from(selectedIds)[0];
    const firstItem = items.find((i) => i.id === firstId);
    return firstItem ? canEmitBoleto(firstItem.status) : false;
  }, [selectedIds, items]);

  // Itens elegíveis: podem mudar status OU emitir boleto
  const eligibleItems = useMemo(
    () =>
      items.filter((item) => {
        const target = getTargetStatus(item.status);
        const canBill = canEmitBoleto(item.status);
        
        // Se não pode fazer nada, não é elegível
        if (!target && !canBill) return false;
        
        // Se há seleção ativa de mudança de status, respeita o grupo ativo
        if (activeTargetStatus !== null) return target === activeTargetStatus;
        
        // Se há seleção ativa de emissão de boleto, só permite outros elegíveis para boleto
        if (canSelectedEmitBoleto) return canBill;
        
        return true;
      }),
    [items, activeTargetStatus, canSelectedEmitBoleto]
  );

  const allEligibleSelected = useMemo(
    () => eligibleItems.length > 0 && eligibleItems.every((item) => selectedIds.has(item.id)),
    [eligibleItems, selectedIds]
  );

  const someEligibleSelected = useMemo(
    () => eligibleItems.some((item) => selectedIds.has(item.id)),
    [eligibleItems, selectedIds]
  );

  // Resumo da seleção atual (sempre será um único grupo com a lógica de travamento)
  const selectionSummary = useMemo(() => {
    const groups = new Map<string, number>();
    Array.from(selectedIds).forEach((id) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const target = getTargetStatus(item.status);
      if (!target) return;
      groups.set(target, (groups.get(target) ?? 0) + 1);
    });
    return groups;
  }, [selectedIds, items]);

  const loadListing = async (currentFilters: FleetListingFilters, page = 0) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      setSelectedIds(new Set());
      setUpdateSuccessMessage(null);
      const data = await fleetListingService.getFleetListing({
        ...currentFilters,
        page,
        size: currentFilters.size ?? 5,
        sort: currentFilters.sort ?? 'createdAt,desc'
      });
      setItems(data.content);
      setPageState(data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Não foi possível carregar a listagem de frotas. Por favor, tente novamente.';
      setErrorMessage(message);
      setItems([]);
      setPageState((prev) => ({ ...prev, content: [], totalElements: 0, totalPages: 0 }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadListing(initialFilters, 0);
  }, []);

  const handleFilterChange = (name: keyof FleetListingFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = async () => {
    const cnpjWithoutMask = unmaskCnpj(cnpj);
    const nextFilters: FleetListingFilters = {
      ...filters,
      dealerNetworkCnpj: cnpjType === 'dealerNetwork' ? cnpjWithoutMask : '',
      dealerCnpj: cnpjType === 'dealer' ? cnpjWithoutMask : '',
      size: filters.size ?? 5,
      sort: 'createdAt,desc'
    };
    setFilters(nextFilters);
    await loadListing(nextFilters, 0);
  };

  const handleClearFilters = async () => {
    const resetFilters: FleetListingFilters = {
      ...initialFilters,
      size: 5,
      sort: 'createdAt,desc'
    };
    setFilters(resetFilters);
    setCnpjType('dealerNetwork');
    setCnpj('');
    await loadListing(resetFilters, 0);
  };

  const handlePrevPage = async () => {
    if (pageState.first || isLoading) return;
    await loadListing(filters, pageState.number - 1);
  };

  const handleNextPage = async () => {
    if (pageState.last || isLoading) return;
    await loadListing(filters, pageState.number + 1);
  };

  const handlePageSizeChange = async (value: string) => {
    const parsedValue = Number(value);
    if (![5, 10, 50, 100].includes(parsedValue)) return;
    const nextFilters: FleetListingFilters = {
      ...filters,
      size: parsedValue,
      sort: filters.sort ?? 'createdAt,desc'
    };
    setFilters(nextFilters);
    await loadListing(nextFilters, 0);
  };

  // ── Seleção ──────────────────────────────────────────────────────────────

  const handleToggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleItems.map((i) => i.id)));
    }
  };

  const handleToggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Atualização de status ─────────────────────────────────────────────────

  // Aplica atualizações de status em lote localmente (id → novoStatus)
  const applyStatusUpdates = (updates: Map<string, string>) => {
    setItems((prev) =>
      prev.map((item) => {
        const newStatus = updates.get(item.id);
        return newStatus !== undefined ? { ...item, status: newStatus } : item;
      })
    );
    setSelectedIds(new Set());
  };

  const handleUpdateIndividual = async (id: string, targetStatus: string) => {
    try {
      setUpdatingId(id);
      setErrorMessage(null);
      setUpdateSuccessMessage(null);
      await fleetListingService.updateBillingStatus(id, targetStatus);
      applyStatusUpdates(new Map([[id, targetStatus]]));
      setUpdateSuccessMessage(`Status atualizado para "${targetStatus}" com sucesso.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível atualizar o status. Por favor, tente novamente.';
      setErrorMessage(message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;

    // Agrupa ids por status alvo
    const groups = new Map<string, string[]>();
    Array.from(selectedIds).forEach((id) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const target = getTargetStatus(item.status);
      if (!target) return;
      const list = groups.get(target) ?? [];
      list.push(id);
      groups.set(target, list);
    });

    try {
      setIsBulkUpdating(true);
      setErrorMessage(null);
      setUpdateSuccessMessage(null);

      const allUpdates = new Map<string, string>();
      for (const [targetStatus, ids] of Array.from(groups.entries())) {
        await fleetListingService.updateBillingStatusBulk(ids, targetStatus);
        ids.forEach((id) => allUpdates.set(id, targetStatus));
      }

      applyStatusUpdates(allUpdates);

      const summaryParts = Array.from(groups.entries()).map(
        ([target, ids]) => `${ids.length} → ${target}`
      );
      setUpdateSuccessMessage(
        `${allUpdates.size} registro(s) atualizado(s) com sucesso: ${summaryParts.join(', ')}.`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível atualizar os registros. Por favor, tente novamente.';
      setErrorMessage(message);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // ── Emissão de boleto (fechamento de fatura) ───────────────────────────────

  const handleOpenBillingModal = () => {
    setBillingError(null);
    setBillingResult(null);
    const due = new Date();
    due.setDate(due.getDate() + 7);
    setBillingDueDate(due.toISOString().slice(0, 10));
    setIsBillingModalOpen(true);
  };

  const handleEmitBoleto = async () => {
    if (selectedIds.size === 0) return;
    if (!billingDueDate) {
      setBillingError('Informe a data de vencimento.');
      return;
    }
    try {
      setIsBilling(true);
      setBillingError(null);
      const boleto = await billingService.closeBatch({
        transactionIds: Array.from(selectedIds),
        dataVencimento: billingDueDate
      });
      setBillingResult(boleto);
      setSelectedIds(new Set());
      await loadListing(filters, pageState.number);
    } catch (error) {
      setBillingError(error instanceof Error ? error.message : 'Não foi possível emitir o boleto. Por favor, tente novamente.');
    } finally {
      setIsBilling(false);
    }
  };

  // ── Relatório ─────────────────────────────────────────────────────────────

  const handleOpenReportModal = () => {
    setReportForm((prev) => ({
      ...prev,
      start: filters.start || '',
      end: filters.end || ''
    }));
    setReportModalMessage(null);
    setIsReportModalOpen(true);
  };

  const handleGenerateReport = async () => {
    if (reportForm.period === 'FROM' && (!reportForm.start || !reportForm.end)) {
      setReportModalMessage('Para período por intervalo, informe Data inicial e Data final.');
      return;
    }

    if (reportForm.period === 'FROM') {
      const startDate = parseDateOnlyToUtc(reportForm.start);
      const endDate = parseDateOnlyToUtc(reportForm.end);

      if (!startDate || !endDate) {
        setReportModalMessage('Datas inválidas. Use o formato correto.');
        return;
      }

      if (startDate.getTime() > endDate.getTime()) {
        setReportModalMessage('A data inicial não pode ser maior que a data final.');
        return;
      }

      const oneDayMs = 24 * 60 * 60 * 1000;
      const intervalDays = Math.floor((endDate.getTime() - startDate.getTime()) / oneDayMs) + 1;
      if (intervalDays > 45) {
        setReportModalMessage('O intervalo máximo permitido para relatório é de 45 dias.');
        return;
      }
    }

    try {
      setIsGeneratingReport(true);
      setReportModalMessage(null);
      setErrorMessage(null);
      await fleetListingService.downloadVehiclesReport({
        period: reportForm.period,
        start: reportForm.period === 'FROM' ? (reportForm.start || undefined) : undefined,
        end: reportForm.period === 'FROM' ? (reportForm.end || undefined) : undefined
      });
      setIsReportModalOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível gerar o relatório. Verifique os filtros e tente novamente.';
      setReportModalMessage(message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // ── Helpers de estilo ─────────────────────────────────────────────────────

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'Aguardando Faturamento':
        return { bg: '#FFF3CD', color: '#856404', border: '1px solid #FFEAA0' };
      case 'Faturado':
        return { bg: '#D1FAE5', color: '#065F46', border: '1px solid #A7F3D0' };
      case 'Pago':
        return { bg: '#EDE9FE', color: '#5B21B6', border: '1px solid #C4B5FD' };
      case 'Conciliado':
        return { bg: '#DBEAFE', color: '#1E40AF', border: '1px solid #BFDBFE' };
      default:
        return { bg: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' };
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '0.375rem',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    padding: '0 0.75rem',
    fontSize: '0.95rem'
  };

  const isAnythingUpdating = isBulkUpdating || updatingId !== null;

  return (
    <Box bg="#F5F5F5" minH="100vh" display="flex" flexDirection="column">
      <Header title="Sistema de Efetivação de Apólices" subtitle="Fleet Listing (Admin)" />

      <Box py={8} px={4} display="flex" justifyContent="center">
        <Box
          maxW={{ base: '98%', md: '95%', lg: '92%', xl: '88%' }}
          w="100%"
          bg="white"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
        >
          <Box p={{ base: 4, md: 6 }}>
            <VStack gap={5} alignItems="stretch">
              {/* Cabeçalho */}
              <Flex
                justifyContent="space-between"
                alignItems={{ base: 'flex-start', md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={2}
              >
                <Heading size="lg" color="#1A1A1A">
                  Listagem de Frotas
                </Heading>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  {pageState.totalElements} registro(s) encontrado(s)
                </Text>
              </Flex>

              {/* Filtros */}
              <Box p={4} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                  gap={3}
                >
                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Buscar por CNPJ
                    </Text>
                    <Flex gap={6} mb={2} wrap="wrap">
                      <Flex as="label" alignItems="center" gap={2} cursor="pointer">
                        <input
                          type="radio"
                          name="cnpjType"
                          checked={cnpjType === 'dealerNetwork'}
                          onChange={() => setCnpjType('dealerNetwork')}
                        />
                        <Text fontSize="sm" color="gray.700">
                          CNPJ da Rede
                        </Text>
                      </Flex>
                      <Flex as="label" alignItems="center" gap={2} cursor="pointer">
                        <input
                          type="radio"
                          name="cnpjType"
                          checked={cnpjType === 'dealer'}
                          onChange={() => setCnpjType('dealer')}
                        />
                        <Text fontSize="sm" color="gray.700">
                          CNPJ da Concessionária
                        </Text>
                      </Flex>
                    </Flex>
                    <Input
                      value={cnpj}
                      onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                      placeholder={
                        cnpjType === 'dealerNetwork'
                          ? 'Ex.: 12.345.678/0001-99'
                          : 'Ex.: 98.765.432/0001-55'
                      }
                      maxLength={18}
                      bg="white"
                    />
                  </GridItem>

                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Status
                    </Text>
                    <select
                      value={filters.status || ''}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Todos</option>
                      <option value="Estoque">Estoque</option>
                      <option value="Aguardando Faturamento">Aguardando Faturamento</option>
                      <option value="Faturado">Faturado</option>
                      <option value="Pago">Pago</option>
                      <option value="Conciliado">Conciliado</option>
                    </select>
                  </GridItem>

                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Situação
                    </Text>
                    <select
                      value={filters.situacao || ''}
                      onChange={(e) => handleFilterChange('situacao', e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Todos</option>
                      <option value="Emitido">Emitido</option>
                      <option value="Problema de Emissão">Problema de Emissão</option>
                    </select>
                  </GridItem>

                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Data inicial
                    </Text>
                    <Input
                      type="date"
                      value={filters.start || ''}
                      onChange={(e) => handleFilterChange('start', e.target.value)}
                      bg="white"
                    />
                  </GridItem>

                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Data final
                    </Text>
                    <Input
                      type="date"
                      value={filters.end || ''}
                      onChange={(e) => handleFilterChange('end', e.target.value)}
                      bg="white"
                    />
                  </GridItem>
                </Grid>

                <Flex mt={4} gap={2} justifyContent="flex-end" flexWrap="wrap">
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    onClick={handleOpenReportModal}
                    disabled={isGeneratingReport}
                  >
                    Gerar relatório
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={handleClearFilters}
                    disabled={isLoading || !hasActiveFilters}
                  >
                    Limpar filtros
                  </Button>
                  <Button
                    bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                    color="white"
                    _hover={{ bg: '#2828a3' }}
                    onClick={handleApplyFilters}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Consultando...' : 'Filtrar'}
                  </Button>
                </Flex>
              </Box>

              {/* Mensagem de erro */}
              {errorMessage && (
                <Box
                  p={4}
                  bg="red.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="red.500"
                >
                  <Text color="red.700" fontWeight="600">
                    {errorMessage}
                  </Text>
                </Box>
              )}

              {/* Mensagem de sucesso */}
              {updateSuccessMessage && (
                <Box
                  p={4}
                  bg="green.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="green.500"
                >
                  <Text color="green.700" fontWeight="600">
                    {updateSuccessMessage}
                  </Text>
                </Box>
              )}

              {/* Barra de ação em lote */}
              {someEligibleSelected && (
                <Flex
                  alignItems="center"
                  gap={3}
                  px={4}
                  py={3}
                  bg="blue.50"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="blue.200"
                  flexWrap="wrap"
                >
                  <Box
                    w={5}
                    h={5}
                    borderRadius="sm"
                    bg="#3333CC"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Text color="white" fontSize="xs" fontWeight="700" lineHeight="1">
                      {selectedIds.size}
                    </Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color="blue.800" fontWeight="600">
                      {selectedIds.size === 1 ? '1 registro selecionado' : `${selectedIds.size} registros selecionados`}
                    </Text>
                    {canSelectedEmitBoleto ? (
                      <Text fontSize="xs" color="blue.600" mt="1px">
                        Pronto para emissão de boleto (fechamento de fatura)
                      </Text>
                    ) : selectionSummary.size > 0 ? (
                      <Text fontSize="xs" color="blue.600" mt="1px">
                        {Array.from(selectionSummary.entries())
                          .map(([target, count]) => `${count} → ${target}`)
                          .join(' · ')}
                      </Text>
                    ) : null}
                  </Box>
                  <Flex gap={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="gray"
                      onClick={() => setSelectedIds(new Set())}
                      disabled={isAnythingUpdating || isBilling}
                    >
                      Cancelar
                    </Button>
                    
                    {/* Botão para emitir boleto */}
                    {canSelectedEmitBoleto && (
                      <Button
                        size="sm"
                        bg="linear-gradient(135deg, #059669 0%, #047857 100%)"
                        color="white"
                        _hover={{ bg: '#047857' }}
                        onClick={handleOpenBillingModal}
                        disabled={isAnythingUpdating || isBilling}
                      >
                        {isBilling ? (
                          <Flex alignItems="center" gap={2}>
                            <Spinner size="xs" />
                            <span>Processando...</span>
                          </Flex>
                        ) : (
                          `Emitir boleto (${selectedIds.size})`
                        )}
                      </Button>
                    )}
                    
                    {/* Botão para atualizar status */}
                    {activeTargetStatus && (
                      <Button
                        size="sm"
                        bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                        color="white"
                        _hover={{ bg: '#2828a3' }}
                        onClick={handleBulkUpdate}
                        disabled={isAnythingUpdating || isBilling}
                      >
                        {isBulkUpdating ? (
                          <Flex alignItems="center" gap={2}>
                            <Spinner size="xs" />
                            <span>Atualizando...</span>
                          </Flex>
                        ) : (
                          `Atualizar ${selectedIds.size} registro(s)`
                        )}
                      </Button>
                    )}
                  </Flex>
                </Flex>
              )}

              {/* Tabela */}
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                {isLoading ? (
                  <Flex py={12} justifyContent="center" alignItems="center" gap={2}>
                    <Spinner size="sm" color="#3333CC" />
                    <Text fontSize="sm" color="gray.600">
                      Carregando listagem...
                    </Text>
                  </Flex>
                ) : items.length === 0 ? (
                  <Flex py={12} justifyContent="center" alignItems="center">
                    <Text fontSize="sm" color="gray.600">
                      Nenhum registro encontrado para os filtros informados.
                    </Text>
                  </Flex>
                ) : (
                  <Box overflowX="auto">
                    <Box as="table" width="100%" fontSize="sm">
                      <Box as="thead" bg="gray.100">
                        <Box as="tr">
                          {/* Checkbox de seleção em massa - só aparece quando há seleção ativa */}
                          <Box as="th" p={3} textAlign="center" width="48px">
                            {eligibleItems.length > 0 && selectedIds.size > 0 && (
                              <Flex justifyContent="center">
                                <input
                                  type="checkbox"
                                  checked={allEligibleSelected}
                                  onChange={handleToggleSelectAll}
                                  title={
                                    allEligibleSelected
                                      ? 'Desmarcar todos'
                                      : canSelectedEmitBoleto
                                        ? `Selecionar todos para emissão de boleto (${eligibleItems.length})`
                                        : `Selecionar todos → ${activeTargetStatus} (${eligibleItems.length})`
                                  }
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                              </Flex>
                            )}
                          </Box>
                          <Box as="th" p={3} textAlign="left">Grupo</Box>
                          <Box as="th" p={3} textAlign="left">CNPJ</Box>
                          <Box as="th" p={3} textAlign="left">Razão Social</Box>
                          <Box as="th" p={3} textAlign="left">Placa</Box>
                          <Box as="th" p={3} textAlign="left">Chassi</Box>
                          <Box as="th" p={3} textAlign="right">Prêmio</Box>
                          <Box as="th" p={3} textAlign="left">Data</Box>
                          <Box as="th" p={3} textAlign="left">Status</Box>
                          <Box as="th" p={3} textAlign="left">Situação</Box>
                          <Box as="th" p={3} textAlign="center">Ação</Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {items.map((item) => {
                          const targetStatus = getTargetStatus(item.status);
                          const canBill = canEmitBoleto(item.status);
                          // Elegível se pode mudar status OU emitir boleto
                          const isEligible = targetStatus !== null || canBill;
                          
                          // Checkbox desabilitado se já há uma seleção de outro tipo
                          let isLockedOut = false;
                          if (isEligible && selectedIds.size > 0) {
                            if (canSelectedEmitBoleto) {
                              // Seleção ativa é para boleto: trava itens que não podem emitir boleto
                              isLockedOut = !canBill;
                            } else if (activeTargetStatus !== null) {
                              // Seleção ativa é para mudança de status: trava itens de outro grupo
                              isLockedOut = targetStatus !== activeTargetStatus;
                            }
                          }
                          
                          const isSelected = selectedIds.has(item.id);
                          const isUpdatingThis = updatingId === item.id;
                          const badgeStyle = getStatusBadgeStyle(item.status);

                          return (
                            <Box
                              as="tr"
                              key={item.id}
                              borderTopWidth="1px"
                              borderTopColor="gray.100"
                              bg={isSelected ? 'blue.50' : undefined}
                              _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                              transition="background 0.15s"
                            >
                              {/* Checkbox por linha */}
                              <Box as="td" p={3} textAlign="center">
                                {isEligible && (
                                  <Flex justifyContent="center">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleToggleItem(item.id)}
                                      disabled={isAnythingUpdating || isLockedOut}
                                      title={
                                        isLockedOut
                                          ? canSelectedEmitBoleto
                                            ? 'Conclua ou cancele a seleção atual (emissão de boleto) antes de selecionar itens de outro tipo'
                                            : `Conclua ou cancele a seleção atual (→ ${activeTargetStatus}) antes de selecionar itens de outro grupo`
                                          : undefined
                                      }
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: isLockedOut ? 'not-allowed' : 'pointer',
                                        opacity: isLockedOut ? 0.35 : 1
                                      }}
                                    />
                                  </Flex>
                                )}
                              </Box>
                              <Box as="td" p={3}>{item.grupo || '-'}</Box>
                              <Box as="td" p={3}>{item.cnpjConcessionaria || '-'}</Box>
                              <Box as="td" p={3}>{item.razaoSocialConcessionaria || '-'}</Box>
                              <Box as="td" p={3}>{item.placa || '-'}</Box>
                              <Box as="td" p={3}>{item.chassi || '-'}</Box>
                              <Box as="td" p={3} textAlign="right">
                                {typeof item.valorPremioAcumulado === 'number'
                                  ? currencyFormatter.format(item.valorPremioAcumulado)
                                  : '-'}
                              </Box>
                              <Box as="td" p={3}>{formatDateTime(item.data)}</Box>
                              <Box as="td" p={3}>
                                <Box
                                  as="span"
                                  display="inline-block"
                                  px={2}
                                  py="2px"
                                  borderRadius="full"
                                  fontSize="xs"
                                  fontWeight="600"
                                  bg={badgeStyle.bg}
                                  color={badgeStyle.color}
                                  border={badgeStyle.border}
                                  whiteSpace="nowrap"
                                >
                                  {item.status || '-'}
                                </Box>
                              </Box>
                              <Box as="td" p={3}>{item.situacao || '-'}</Box>
                              {/* Coluna de ação individual */}
                              <Box as="td" p={3} textAlign="center">
                                {isEligible && targetStatus && (() => {
                                  const btnStyle = getStatusBadgeStyle(targetStatus);
                                  const isDisabled = isAnythingUpdating || (someEligibleSelected && !isSelected);
                                  return (
                                    <Button
                                      size="xs"
                                      bg={btnStyle.bg}
                                      color={btnStyle.color}
                                      border={btnStyle.border}
                                      _hover={{ filter: 'brightness(0.93)' }}
                                      _disabled={{ opacity: 0.45, cursor: 'not-allowed', filter: 'none' }}
                                      onClick={() => handleUpdateIndividual(item.id, targetStatus)}
                                      disabled={isDisabled}
                                      title={
                                        someEligibleSelected && !isSelected
                                          ? 'Conclua ou cancele a seleção atual antes de usar ação individual'
                                          : `Marcar como ${targetStatus}`
                                      }
                                      minW="110px"
                                      fontWeight="600"
                                    >
                                      {isUpdatingThis ? (
                                        <Flex alignItems="center" gap={1}>
                                          <Spinner size="xs" />
                                          <span>Atualizando</span>
                                        </Flex>
                                      ) : (
                                        `✓ Marcar ${targetStatus}`
                                      )}
                                    </Button>
                                  );
                                })()}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Legenda de elegibilidade */}
              {eligibleItems.length > 0 && !isLoading && (
                <Flex alignItems="center" gap={4} flexWrap="wrap">
                  {/* Itens que podem emitir boleto */}
                  {(() => {
                    const count = items.filter((i) => canEmitBoleto(i.status)).length;
                    if (count === 0) return null;
                    return (
                      <Flex alignItems="center" gap={1}>
                        <Box
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg="#FFF3CD"
                          border="1px solid #FFEAA0"
                          flexShrink={0}
                        />
                        <Text fontSize="xs" color="gray.500">
                          {count} × <strong>Estoque/Aguardando Faturamento</strong> → emitir boleto
                        </Text>
                      </Flex>
                    );
                  })()}
                  
                  {/* Itens que podem mudar status */}
                  {Object.entries(TRANSITION_MAP).map(([from, to]) => {
                    const count = items.filter((i) => i.status === from).length;
                    if (count === 0) return null;
                    return (
                      <Flex key={from} alignItems="center" gap={1}>
                        <Box
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg={getStatusBadgeStyle(from).bg}
                          border={getStatusBadgeStyle(from).border}
                          flexShrink={0}
                        />
                        <Text fontSize="xs" color="gray.500">
                          {count} × <strong>{from}</strong> → {to}
                        </Text>
                      </Flex>
                    );
                  })}
                </Flex>
              )}

              {/* Paginação */}
              <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
                <Text fontSize="sm" color="gray.600">
                  Página {pageState.totalPages > 0 ? pageState.number + 1 : 0} de{' '}
                  {pageState.totalPages}
                </Text>
                <Flex gap={2} alignItems="center" flexWrap="wrap">
                  <Flex alignItems="center" gap={2}>
                    <Text fontSize="sm" color="gray.600">
                      Itens por página
                    </Text>
                    <select
                      value={String(filters.size ?? 5)}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      disabled={isLoading}
                      style={{
                        height: '36px',
                        borderRadius: '0.375rem',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#FFFFFF',
                        padding: '0 0.5rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="5">5 (padrão)</option>
                      <option value="10">10</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </Flex>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={handlePrevPage}
                    disabled={isLoading || pageState.first}
                  >
                    Anterior
                  </Button>
                  <Button
                    bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                    color="white"
                    _hover={{ bg: '#2828a3' }}
                    onClick={handleNextPage}
                    disabled={isLoading || pageState.last || pageState.totalPages === 0}
                  >
                    Próxima
                  </Button>
                </Flex>
              </Flex>
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* Modal de relatório */}
      {isReportModalOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.45)"
          zIndex={1400}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Box
            w="100%"
            maxW="680px"
            bg="white"
            borderRadius="xl"
            boxShadow="0 10px 30px rgba(0,0,0,0.2)"
            overflow="hidden"
          >
            <Box px={5} py={4} borderBottomWidth="1px" borderBottomColor="gray.100">
              <Heading size="md" color="#1A1A1A">
                Gerar relatório de veículos
              </Heading>
              <Text mt={1} fontSize="sm" color="gray.600">
                Defina os filtros e clique em baixar relatório.
              </Text>
            </Box>

            <Box p={5}>
              <VStack alignItems="stretch" gap={3}>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    mb={1}
                    fontWeight="600"
                    textTransform="uppercase"
                  >
                    Período
                  </Text>
                  <select
                    value={reportForm.period}
                    onChange={(e) =>
                      setReportForm((prev) => ({
                        ...prev,
                        period: e.target.value,
                        start: e.target.value === 'MONTHLY' ? '' : prev.start,
                        end: e.target.value === 'MONTHLY' ? '' : prev.end
                      }))
                    }
                    style={selectStyle}
                  >
                    <option value="MONTHLY">Mensal</option>
                    <option value="FROM">Por período</option>
                  </select>
                </Box>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Data inicial
                    </Text>
                    <Input
                      type="date"
                      value={reportForm.start}
                      onChange={(e) =>
                        setReportForm((prev) => ({ ...prev, start: e.target.value }))
                      }
                      disabled={reportForm.period === 'MONTHLY'}
                      bg="white"
                    />
                  </GridItem>
                  <GridItem>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      Data final
                    </Text>
                    <Input
                      type="date"
                      value={reportForm.end}
                      onChange={(e) =>
                        setReportForm((prev) => ({ ...prev, end: e.target.value }))
                      }
                      disabled={reportForm.period === 'MONTHLY'}
                      bg="white"
                    />
                  </GridItem>
                </Grid>

                <Box
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="blue.500"
                >
                  <Text fontSize="sm" color="blue.800" fontWeight="600">
                    Para período por intervalo, o máximo permitido é de 45 dias.
                  </Text>
                </Box>

                {reportModalMessage && (
                  <Box
                    p={3}
                    bg="red.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderLeftColor="red.500"
                  >
                    <Text fontSize="sm" color="red.700" fontWeight="600">
                      {reportModalMessage}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            <Flex
              px={5}
              py={4}
              borderTopWidth="1px"
              borderTopColor="gray.100"
              justifyContent="flex-end"
              gap={2}
            >
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => {
                  setReportModalMessage(null);
                  setIsReportModalOpen(false);
                }}
                disabled={isGeneratingReport}
              >
                Cancelar
              </Button>
              <Button
                bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                color="white"
                _hover={{ bg: '#2828a3' }}
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? 'Gerando relatório...' : 'Baixar relatório'}
              </Button>
            </Flex>
          </Box>
        </Box>
      )}

      {/* Modal de emissão de boleto (fechamento de fatura) */}
      {isBillingModalOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.45)"
          zIndex={1400}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Box w="100%" maxW="620px" bg="white" borderRadius="xl" boxShadow="0 10px 30px rgba(0,0,0,0.2)" overflow="hidden">
            <Box px={5} py={4} borderBottomWidth="1px" borderBottomColor="gray.100">
              <Heading size="md" color="#1A1A1A">
                {billingResult ? 'Boleto emitido' : 'Emitir boleto (fechamento)'}
              </Heading>
              <Text mt={1} fontSize="sm" color="gray.600">
                {billingResult
                  ? 'O boleto foi gerado e os veículos foram marcados como Faturado.'
                  : 'Confira o resumo da seleção e informe a data de vencimento. Será gerado 1 boleto para o pagador resolvido.'}
              </Text>
            </Box>

            <Box p={5}>
              {!billingResult ? (
                <VStack alignItems="stretch" gap={3}>
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Flex justifyContent="space-between">
                      <Text fontSize="sm" color="gray.600">Veículos selecionados</Text>
                      <Text fontSize="sm" fontWeight="700" color="#1A1A1A">{selectedIds.size}</Text>
                    </Flex>
                    <Flex justifyContent="space-between" mt={1}>
                      <Text fontSize="sm" color="gray.600">Valor total (Σ prêmio)</Text>
                      <Text fontSize="sm" fontWeight="700" color="#1A1A1A">
                        {currencyFormatter.format(
                          items
                            .filter((i) => selectedIds.has(i.id))
                            .reduce((sum, i) => sum + (i.valorPremioAcumulado || 0), 0)
                        )}
                      </Text>
                    </Flex>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase">
                      Data de vencimento
                    </Text>
                    <Input type="date" value={billingDueDate} onChange={(e) => setBillingDueDate(e.target.value)} bg="white" />
                  </Box>

                  <Box p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.500">
                    <Text fontSize="sm" color="blue.800">
                      A seleção precisa ser de um único pagador. Se houver mistura (dealers/redes diferentes), o backend rejeita.
                    </Text>
                  </Box>

                  {billingError && (
                    <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
                      <Text fontSize="sm" color="red.700" fontWeight="600">{billingError}</Text>
                    </Box>
                  )}
                </VStack>
              ) : (
                <VStack alignItems="stretch" gap={2}>
                  <Flex justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">Status</Text>
                    <Text fontSize="sm" fontWeight="700" color="#047857">{billingResult.status}</Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">Valor</Text>
                    <Text fontSize="sm" fontWeight="700">{currencyFormatter.format(billingResult.valorTotal)}</Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">Fatura</Text>
                    <Text fontSize="sm" fontWeight="700" fontFamily="mono">{billingResult.numeroFatura ?? '-'}</Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">Nº do boleto</Text>
                    <Text fontSize="sm" fontWeight="700">{billingResult.numeroBoleto || '-'}</Text>
                  </Flex>
                  <Box mt={1}>
                    <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase">
                      Linha digitável
                    </Text>
                    {!billingResult.linhaDigitavel && !billingResult.codigoBarras ? (
                      <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
                        <Text fontSize="sm" color="red.700" fontWeight="700">
                          Emissão sem linha digitável — localize o boleto {billingResult.numeroFatura ?? '(sem número)'} em Boletos e use Sincronizar
                        </Text>
                      </Box>
                    ) : (
                      <Flex gap={2} alignItems="center">
                        <Box flex="1" p={2} bg="gray.50" borderRadius="md" fontFamily="mono" fontSize="sm" wordBreak="break-all">
                          {billingResult.linhaDigitavel || billingResult.codigoBarras}
                        </Box>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const value = billingResult.linhaDigitavel || billingResult.codigoBarras || '';
                            if (value) navigator.clipboard?.writeText(value);
                          }}
                        >
                          Copiar
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </VStack>
              )}
            </Box>

            <Flex px={5} py={4} borderTopWidth="1px" borderTopColor="gray.100" justifyContent="flex-end" gap={2}>
              {!billingResult ? (
                <>
                  <Button variant="outline" colorScheme="gray" onClick={() => setIsBillingModalOpen(false)} disabled={isBilling}>
                    Cancelar
                  </Button>
                  <Button
                    bg="linear-gradient(135deg, #059669 0%, #047857 100%)"
                    color="white"
                    _hover={{ bg: '#047857' }}
                    onClick={handleEmitBoleto}
                    disabled={isBilling}
                  >
                    {isBilling ? 'Emitindo...' : 'Emitir boleto'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" colorScheme="gray" onClick={() => billingService.downloadBoletoPdf(billingResult.id)}>
                    Baixar PDF
                  </Button>
                  <Button
                    bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                    color="white"
                    _hover={{ bg: '#2828a3' }}
                    onClick={() => setIsBillingModalOpen(false)}
                  >
                    Fechar
                  </Button>
                </>
              )}
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
};
